import React, { createContext, FC, useCallback, useMemo, useState } from 'react';
import { IDataTableRequiredView, IDataTableRow } from '../models';
import { DataTableCell, DataTableCellWrapper } from '../_Cell/DataTableCell';
import { DataTableBodyRowContext } from './context';
import { useList, useStore } from 'effector-react';
import { $columnWidthsStore, $columnsStore, ACTIONS_COL } from '../store/columns';
import { classnames } from '@src/utils/classnames';
import { useRowWidthStyles } from '../hooks';
import { $rowsStore } from '../store/rows';
import { DATA_ELEM_ROW } from '../consts';

import styles from './DataTableBodyRow.module.scss';
import { getClasses } from '@src/utils/getClass';
import { propsHooks } from '@src/hooks/propsHooks';
import { DataTableBodyRowAction } from './_Action/DataTableBodyRowAction';
import { Box } from '@src/atoms/Box';

interface IDataTableBodyRowProps extends IDataTableRequiredView {
  row: IDataTableRow;
  rowIdField?: string;
  isSubRow?: boolean;
  nestLevel?: number;
}

interface IProps extends IDataTableRequiredView {
  rowIdField?: string;
}

export const RowContext = createContext<{ collapse: boolean; changeCollapse: (v: boolean) => void }>({
  collapse: false,
  changeCollapse: () => {},
});

const DataTableBodyRow: FC<IDataTableBodyRowProps> = ({ row, view, rowIdField, isSubRow, nestLevel = 0 }) => {
  const columns = useStore($columnsStore);
  const columnWidthsStore = useStore($columnWidthsStore);
  const [collapse, changeCollapse] = useState(row.collapse || false);

  const { isHovered, hoveredCallbacks } = propsHooks.useHovered();
  const rowStyle = useRowWidthStyles();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      row.onClick?.(row, e);
    },
    [row],
  );

  let firstIndex: number | null = null;

  const contextValue = useMemo(() => ({ collapse, changeCollapse }), [collapse]);

  return (
    <RowContext.Provider value={contextValue}>
      <div>
        <div
          style={rowStyle}
          data-elem={DATA_ELEM_ROW}
          data-row-uniq-id={row.data?.[rowIdField ?? 'id']?.rawValue}
          onClick={handleClick}
          {...hoveredCallbacks}
        >
          <DataTableBodyRowContext.Provider value={{ hovered: isHovered, view }}>
            {columns.map((column, index) => {
              const data = row.data[column.id];
              if (data && firstIndex === null) {
                firstIndex = index;
              }

              return (
                <DataTableCell
                  key={column.id}
                  column={column}
                  row={row}
                  isSubRow={isSubRow}
                  index={firstIndex !== null && index === firstIndex ? firstIndex : undefined}
                  nestLevel={nestLevel}
                />
              );
            })}

            {columnWidthsStore.has(ACTIONS_COL) && (
              <DataTableCellWrapper>
                {row.actions && (
                  <DataTableBodyRowAction rowData={row.data} actions={row.actions} isDisplay={isHovered} />
                )}
              </DataTableCellWrapper>
            )}
          </DataTableBodyRowContext.Provider>
        </div>
        {collapse &&
          row.subRows &&
          row.subRows.map((r, idx) => (
            <DataTableBodyRow key={idx} row={r} view={view} isSubRow nestLevel={nestLevel + 1} />
          ))}
      </div>
    </RowContext.Provider>
  );
};

export const DataTableBodyRows = ({ view, rowIdField }: IProps) => {
  const wrapperClasses = classnames(styles.root, ...getClasses(styles, { view }));
  const rowsList = useList($rowsStore, (row, idx) => {
    return (
      <Box column key={idx} className={wrapperClasses}>
        <DataTableBodyRow row={row} view={view} rowIdField={rowIdField} />
      </Box>
    );
  });

  return rowsList;
};
